import React, { useState, useEffect } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Plus,
  Trash2,
  GripVertical,
  Sparkles,
  BookOpen,
  Target,
  Lightbulb,
  Save,
  Download,
  Calendar,
  Clock,
  Edit,
  X,
  CheckCircle2,
  Zap,
} from 'lucide-react';
import { checkLearningAccess } from '../utils/learningAccess';
import { saveRoadmapToPortal } from '../utils/saveRoadmap';
import RoadmapTimeline from './RoadmapTimeline';

// Template library for Pro users
const ROADMAP_TEMPLATES = {
  'devsecops-fast': {
    name: 'DevSecOps Fast Track',
    roleKey: 'devsecops',
    description: 'Accelerated 4-month path for experienced developers',
    items: [
      { id: '1', title: 'Security Fundamentals', duration: 2, month: 1, description: 'Core security principles and practices', type: 'skill' },
      { id: '2', title: 'CI/CD Security', duration: 3, month: 1, description: 'Secure pipeline implementation', type: 'skill' },
      { id: '3', title: 'Infrastructure as Code Security', duration: 4, month: 2, description: 'Terraform, CloudFormation security', type: 'skill' },
      { id: '4', title: 'Container Security', duration: 3, month: 2, description: 'Docker, Kubernetes security', type: 'skill' },
      { id: '5', title: 'AWS Security Specialty', duration: 4, month: 3, description: 'Cloud security best practices', type: 'certification', cost: 300 },
      { id: '6', title: 'Advanced Threat Modeling', duration: 3, month: 4, description: 'Security architecture and design', type: 'skill' },
    ],
    totalMonths: 4,
  },
  'devsecops-comprehensive': {
    name: 'DevSecOps Comprehensive',
    roleKey: 'devsecops',
    description: 'Complete 8-month journey from basics to advanced',
    items: [
      { id: '1', title: 'Linux & Networking Basics', duration: 4, month: 1, description: 'Foundation skills', type: 'skill' },
      { id: '2', title: 'Cloud Fundamentals', duration: 4, month: 1, description: 'AWS, Azure, GCP basics', type: 'skill' },
      { id: '3', title: 'DevOps Essentials', duration: 4, month: 2, description: 'CI/CD, Git, Docker', type: 'skill' },
      { id: '4', title: 'Security Fundamentals', duration: 4, month: 2, description: 'Core security concepts', type: 'skill' },
      { id: '5', title: 'CompTIA Security+', duration: 6, month: 3, description: 'Industry certification', type: 'certification', cost: 240 },
      { id: '6', title: 'Infrastructure as Code', duration: 4, month: 4, description: 'Terraform, Ansible', type: 'skill' },
      { id: '7', title: 'Kubernetes Security', duration: 4, month: 5, description: 'Container orchestration security', type: 'skill' },
      { id: '8', title: 'AWS Security Specialty', duration: 4, month: 6, description: 'Advanced cloud security', type: 'certification', cost: 300 },
      { id: '9', title: 'Threat Modeling', duration: 3, month: 7, description: 'Security architecture', type: 'skill' },
      { id: '10', title: 'Compliance & GRC', duration: 4, month: 8, description: 'Regulatory compliance', type: 'skill' },
    ],
    totalMonths: 8,
  },
};

// Smart suggestions based on role and experience
const SMART_SUGGESTIONS = {
  beginner: [
    { title: 'Linux Fundamentals', description: 'Essential for any DevOps role', duration: 4, type: 'skill' },
    { title: 'Git & Version Control', description: 'Foundation of modern development', duration: 2, type: 'skill' },
    { title: 'Cloud Basics (AWS)', description: 'Start with AWS fundamentals', duration: 4, type: 'skill' },
  ],
  intermediate: [
    { title: 'CI/CD Pipelines', description: 'Automate your deployments', duration: 4, type: 'skill' },
    { title: 'Container Orchestration', description: 'Kubernetes or Docker Swarm', duration: 6, type: 'skill' },
    { title: 'Infrastructure as Code', description: 'Terraform or CloudFormation', duration: 4, type: 'skill' },
  ],
  advanced: [
    { title: 'Security Architecture', description: 'Design secure systems', duration: 6, type: 'skill' },
    { title: 'Advanced Kubernetes', description: 'Production-grade orchestration', duration: 4, type: 'skill' },
    { title: 'Cloud Security Specialty', description: 'AWS/Azure security certification', duration: 6, type: 'certification', cost: 300 },
  ],
};

function SortableItem({ item, onEdit, onDelete, month }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isCert = item.type === 'certification';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rounded-xl border p-4 mb-3 ${
        isCert
          ? 'border-green-400/30 bg-green-400/5'
          : 'border-slate-700 bg-slate-900/50'
      } ${isDragging ? 'shadow-2xl' : ''}`}
    >
      <div className="flex items-start gap-3">
        <div
          {...attributes}
          {...listeners}
          className="flex-shrink-0 pt-1 cursor-grab active:cursor-grabbing"
        >
          <GripVertical className="h-5 w-5 text-slate-500" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <div className="font-extrabold text-white">{item.title}</div>
            {isCert && (
              <span className="px-2 py-0.5 rounded-full bg-green-400/10 border border-green-400/20 text-xs font-bold text-green-300">
                Certification
              </span>
            )}
          </div>
          <div className="text-sm text-slate-300 mb-2">{item.description || 'No description'}</div>
          <div className="flex items-center gap-4 text-xs text-slate-400">
            <div className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              Month {item.month || month}
            </div>
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {item.duration} week{item.duration !== 1 ? 's' : ''}
            </div>
            {isCert && item.cost && (
              <div className="flex items-center gap-1">
                <span>£{item.cost}</span>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onEdit(item)}
            className="p-2 rounded-lg border border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700 transition-colors"
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => onDelete(item.id)}
            className="p-2 rounded-lg border border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function ItemEditor({ item, onSave, onCancel, months }) {
  const [formData, setFormData] = useState({
    title: item?.title || '',
    description: item?.description || '',
    duration: item?.duration || 4,
    month: item?.month || 1,
    type: item?.type || 'skill',
    cost: item?.cost || 0,
  });

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="text-xl font-extrabold text-white">
            {item ? 'Edit Item' : 'Add New Item'}
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="p-2 rounded-lg border border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-bold text-slate-300 mb-2">Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-2 text-white focus:border-green-400 focus:outline-none"
              placeholder="e.g., Learn Kubernetes"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-slate-300 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-2 text-white focus:border-green-400 focus:outline-none"
              placeholder="What will you learn in this item?"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-slate-300 mb-2">Month</label>
              <select
                value={formData.month}
                onChange={(e) => setFormData({ ...formData, month: parseInt(e.target.value) })}
                className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-2 text-white focus:border-green-400 focus:outline-none"
              >
                {Array.from({ length: months }, (_, i) => i + 1).map((m) => (
                  <option key={m} value={m}>
                    Month {m}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-bold text-slate-300 mb-2">Duration (weeks)</label>
              <input
                type="number"
                min="1"
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) || 1 })}
                className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-2 text-white focus:border-green-400 focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold text-slate-300 mb-2">Type</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-2 text-white focus:border-green-400 focus:outline-none"
            >
              <option value="skill">Skill</option>
              <option value="certification">Certification</option>
            </select>
          </div>

          {formData.type === 'certification' && (
            <div>
              <label className="block text-sm font-bold text-slate-300 mb-2">Cost (£)</label>
              <input
                type="number"
                min="0"
                value={formData.cost}
                onChange={(e) => setFormData({ ...formData, cost: parseFloat(e.target.value) || 0 })}
                className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-2 text-white focus:border-green-400 focus:outline-none"
              />
            </div>
          )}

          <div className="flex items-center gap-3 pt-4">
            <button
              type="button"
              onClick={() => onSave(formData)}
              className="flex-1 rounded-xl bg-green-400 px-6 py-3 font-bold text-slate-950 hover:bg-green-300 transition-colors"
            >
              <Save className="h-4 w-4 inline mr-2" />
              Save Item
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 rounded-xl border border-slate-700 bg-slate-800 px-6 py-3 font-bold text-slate-300 hover:bg-slate-700 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ProRoadmapGenerator({ roleKey, onRoadmapGenerated }) {
  const [isPro, setIsPro] = useState(false);
  const [loading, setLoading] = useState(true);
  const [roadmap, setRoadmap] = useState(null);
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [totalMonths, setTotalMonths] = useState(6);
  const [editingItem, setEditingItem] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [experienceLevel, setExperienceLevel] = useState('intermediate');
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    checkLearningAccess().then((pro) => {
      setIsPro(pro);
      setLoading(false);
    });
  }, []);

  // Debug: Log when roadmap state changes
  useEffect(() => {
    if (roadmap) {
      console.log('Roadmap state updated:', roadmap);
    }
  }, [roadmap]);

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const reordered = arrayMove(items, oldIndex, newIndex);
        // Update order property to reflect new position
        return reordered.map((item, idx) => ({ ...item, order: idx }));
      });
    }
    setActiveId(null);
  };

  const handleAddItem = () => {
    const newItem = {
      id: `item-${Date.now()}`,
      title: 'New Item',
      description: '',
      duration: 4,
      month: 1,
      type: 'skill',
      order: items.length, // Preserve order for drag and drop
    };
    setEditingItem(newItem);
  };

  const handleEditItem = (item) => {
    setEditingItem({ ...item });
  };

  const handleSaveItem = (itemData) => {
    if (editingItem?.id && items.find((i) => i.id === editingItem.id)) {
      // Update existing - preserve order
      const existingItem = items.find((i) => i.id === editingItem.id);
      setItems(items.map((item) => (item.id === editingItem.id ? { ...itemData, id: editingItem.id, order: existingItem.order ?? item.order ?? items.indexOf(item) } : item)));
    } else {
      // Add new - assign order based on current position
      setItems([...items, { ...itemData, id: editingItem?.id || `item-${Date.now()}`, order: items.length }]);
    }
    setEditingItem(null);
  };

  const handleDeleteItem = (itemId) => {
    setItems(items.filter((item) => item.id !== itemId));
  };

  const handleLoadTemplate = (templateKey) => {
    const template = ROADMAP_TEMPLATES[templateKey];
    if (template) {
      setTitle(template.name);
      setSubtitle(template.description);
      setItems(template.items.map((item, idx) => ({ ...item, order: idx })));
      setTotalMonths(template.totalMonths);
      setSelectedTemplate(templateKey);
    }
  };

  const handleAddSuggestion = (suggestion) => {
    const newItem = {
      id: `item-${Date.now()}`,
      ...suggestion,
      month: Math.min(totalMonths, Math.ceil(items.length / 2) + 1),
      order: items.length, // Preserve order
    };
    setItems([...items, newItem]);
  };

  const handleGenerateRoadmap = () => {
    if (items.length === 0) {
      alert('Please add at least one item to generate a roadmap.');
      return;
    }
    
    try {
      console.log('Generating roadmap with items:', items);
      
      // Convert items to format expected by RoadmapTimeline
      // Map month + duration to startMonth/endMonth, and preserve order
      const convertedItems = items.map((item, idx) => {
        const startMonth = item.month || 1;
        const duration = item.duration || 4;
        const weeksPerMonth = 4;
        const monthsNeeded = Math.ceil(duration / weeksPerMonth);
        const endMonth = startMonth + monthsNeeded - 1;
        
        return {
          ...item,
          startMonth,
          endMonth,
          order: item.order !== undefined ? item.order : idx, // Preserve drag-and-drop order
        };
      });
      
      const roadmapData = {
        items: convertedItems,
        totalMonths,
        timelineMessage: `Your ${totalMonths}-month ${title || 'custom'} roadmap`,
      };
      
      console.log('Setting roadmap data:', roadmapData);
      setRoadmap(roadmapData);
      console.log('Roadmap state should now be set');
      
      // Scroll to top to show the preview
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error generating roadmap:', error);
      alert('Failed to generate roadmap. Please check the console for details.');
    }
  };

  const handleSaveToPortal = async () => {
    if (!isPro) return;
    setSaving(true);
    try {
      const result = await saveRoadmapToPortal({
        roadmap: { items, totalMonths },
        roleKey: roleKey || 'custom',
        title: title || 'My Custom Roadmap',
        subtitle: subtitle || '',
        totalMonths,
      });
      if (result.success) {
        alert('Roadmap saved to portal!');
        // Optionally call the callback to refresh the portal
        if (onRoadmapGenerated) {
          onRoadmapGenerated(result.roadmapRun);
        }
      } else {
        // Check if it's a DataStore initialization error
        if (result.error && result.error.includes('IDBDatabase') || result.error.includes('object stores')) {
          alert('Database not ready. Please refresh the page and try again. If the issue persists, try clearing your browser cache.');
        } else {
          alert(`Failed to save: ${result.error}`);
        }
      }
    } catch (error) {
      console.error('Error saving roadmap:', error);
      const errorMessage = error.message || 'Unknown error';
      if (errorMessage.includes('IDBDatabase') || errorMessage.includes('object stores')) {
        alert('Database not ready. Please refresh the page and try again. If the issue persists, try clearing your browser cache.');
      } else {
        alert(`Failed to save roadmap: ${errorMessage}`);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-slate-400">Loading...</div>
      </div>
    );
  }

  if (!isPro) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white flex items-center justify-center p-4">
        <div className="max-w-md text-center">
          <Zap className="h-16 w-16 text-yellow-400 mx-auto mb-4" />
          <h2 className="text-2xl font-extrabold mb-2">Free account required</h2>
          <p className="text-slate-300 mb-6">
            Sign in to your free Tekyu account to use:
          </p>
          <ul className="text-left text-slate-400 mb-6 space-y-2">
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-400" />
              Drag-and-drop reordering
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-400" />
              Template library
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-400" />
              Smart suggestions
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-400" />
              Custom item creation
            </li>
          </ul>
          <a
            href="/portal"
            className="inline-block rounded-xl bg-green-400 px-6 py-3 font-bold text-slate-950 hover:bg-green-300 transition-colors"
          >
            Sign in / join free
          </a>
        </div>
      </div>
    );
  }

  if (roadmap) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="mb-6 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setRoadmap(null)}
              className="text-sm font-bold text-slate-400 hover:text-white transition-colors"
            >
              ← Back to Editor
            </button>
            <button
              type="button"
              onClick={handleSaveToPortal}
              disabled={saving}
              className="rounded-xl bg-green-400 px-6 py-3 font-bold text-slate-950 hover:bg-green-300 transition-colors disabled:opacity-60"
            >
              {saving ? 'Saving...' : 'Save to Portal'}
            </button>
          </div>
          <RoadmapTimeline
            roadmap={roadmap}
            title={title || 'My Custom Roadmap'}
            subtitle={subtitle}
            isUnlocked={true}
            roleKey={roleKey || 'custom'}
            showBootcampLinks={false}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="h-6 w-6 text-green-400" />
            <h1 className="text-3xl font-extrabold bg-gradient-to-r from-green-400 to-green-300 bg-clip-text text-transparent">
              Roadmap Builder
            </h1>
          </div>
          <p className="text-slate-400">Create a custom roadmap with full control over every detail</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Editor */}
          <div className="lg:col-span-2 space-y-6">
            {/* Roadmap Metadata */}
            <div className="rounded-2xl border border-slate-800 bg-slate-950/40 p-6">
              <h2 className="text-xl font-extrabold mb-4">Roadmap Details</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-slate-300 mb-2">Title</label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-white focus:border-green-400 focus:outline-none"
                    placeholder="e.g., My DevSecOps Journey"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-300 mb-2">Subtitle</label>
                  <input
                    type="text"
                    value={subtitle}
                    onChange={(e) => setSubtitle(e.target.value)}
                    className="w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-white focus:border-green-400 focus:outline-none"
                    placeholder="Optional description"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-300 mb-2">Total Months</label>
                  <input
                    type="number"
                    min="1"
                    max="24"
                    value={totalMonths}
                    onChange={(e) => setTotalMonths(parseInt(e.target.value) || 6)}
                    className="w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-white focus:border-green-400 focus:outline-none"
                  />
                </div>
              </div>
            </div>

            {/* Items List */}
            <div className="rounded-2xl border border-slate-800 bg-slate-950/40 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-extrabold">Roadmap Items</h2>
                <button
                  type="button"
                  onClick={handleAddItem}
                  className="inline-flex items-center gap-2 rounded-xl bg-green-400 px-4 py-2 text-sm font-bold text-slate-950 hover:bg-green-300 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Add Item
                </button>
              </div>

              {items.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No items yet. Add items manually or load a template.</p>
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
                    {items.map((item) => (
                      <SortableItem
                        key={item.id}
                        item={item}
                        onEdit={handleEditItem}
                        onDelete={handleDeleteItem}
                        month={item.month}
                      />
                    ))}
                  </SortableContext>
                  <DragOverlay>
                    {activeId ? (
                      <div className="rounded-xl border border-green-400 bg-green-400/10 p-4">
                        {items.find((i) => i.id === activeId)?.title}
                      </div>
                    ) : null}
                  </DragOverlay>
                </DndContext>
              )}

              {items.length > 0 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('Button clicked, items:', items);
                    handleGenerateRoadmap();
                  }}
                  className="mt-6 w-full rounded-xl bg-gradient-to-r from-green-400 to-green-500 px-6 py-3 font-extrabold text-slate-950 hover:from-green-300 hover:to-green-400 transition-colors"
                >
                  <Target className="h-5 w-5 inline mr-2" />
                  Generate Roadmap Preview
                </button>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Templates */}
            <div className="rounded-2xl border border-slate-800 bg-slate-950/40 p-6">
              <h3 className="text-lg font-extrabold mb-4 flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-green-400" />
                Templates
              </h3>
              <div className="space-y-2">
                {Object.entries(ROADMAP_TEMPLATES).map(([key, template]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => handleLoadTemplate(key)}
                    className={`w-full text-left rounded-xl border p-3 transition-colors ${
                      selectedTemplate === key
                        ? 'border-green-400 bg-green-400/10'
                        : 'border-slate-700 bg-slate-900/50 hover:border-slate-600'
                    }`}
                  >
                    <div className="font-bold text-white mb-1">{template.name}</div>
                    <div className="text-xs text-slate-400">{template.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Smart Suggestions */}
            <div className="rounded-2xl border border-slate-800 bg-slate-950/40 p-6">
              <h3 className="text-lg font-extrabold mb-4 flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-yellow-400" />
                Smart Suggestions
              </h3>
              <div className="mb-4">
                <label className="block text-xs font-bold text-slate-300 mb-2">Experience Level</label>
                <select
                  value={experienceLevel}
                  onChange={(e) => setExperienceLevel(e.target.value)}
                  className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white focus:border-green-400 focus:outline-none"
                >
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>
              <div className="space-y-2">
                {SMART_SUGGESTIONS[experienceLevel].map((suggestion, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handleAddSuggestion(suggestion)}
                    className="w-full text-left rounded-xl border border-slate-700 bg-slate-900/50 p-3 hover:border-slate-600 transition-colors"
                  >
                    <div className="font-bold text-white text-sm mb-1">{suggestion.title}</div>
                    <div className="text-xs text-slate-400">{suggestion.description}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {editingItem && (
        <ItemEditor
          item={editingItem}
          onSave={handleSaveItem}
          onCancel={() => setEditingItem(null)}
          months={totalMonths}
        />
      )}
    </div>
  );
}
